import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { BatteryHealthStatus } from '../models'

interface StatusItem {
  title: string
  textI18nKey: string
  valueKeys?: { [key: string]: { highVoltage: string; lowVoltage: string } }
  values?: { [key: string]: Array<string | number> }
}

/**
 * Hook to deliver the contents for the battery health status section
 */
export const useBatteryStatusItems = (batteryHealth: BatteryHealthStatus | null): { statusItems: StatusItem[]; isLowVoltageBattery: boolean } => {
  const { t } = useCustomTranslation()
  const isLowVoltageBattery = batteryHealth?.batteryType[0] === 'ATL LV'

  if (!batteryHealth) return { statusItems: [], isLowVoltageBattery: false }

  const statusItems: StatusItem[] = [
    {
      title: t('system-settings.battery.status.explanation.title'),
      textI18nKey: 'system-settings.battery.status.explanation.text',
    },
    {
      title: t('system-settings.battery.status.remaining-capacity.title'),
      textI18nKey: 'system-settings.battery.status.remaining-capacity.text',
      valueKeys: {
        remainingCapacity: {
          lowVoltage: 'system-settings.battery.status.remaining-capacity.text-low-voltage',
          highVoltage: 'system-settings.battery.status.remaining-capacity.text-high-voltage',
        },
      },
      values: {
        remainingCapacity: batteryHealth.remainingCapacity,
      },
    },
    {
      title: t('system-settings.battery.status.number-of-cycles.title'),
      textI18nKey: 'system-settings.battery.status.number-of-cycles.text',
      valueKeys: {
        numberOfCycles: {
          highVoltage: 'system-settings.battery.status.number-of-cycles.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.number-of-cycles.text-low-voltage',
        },
      },
      values: {
        numberOfCycles: batteryHealth.numberOfCycles,
      },
    },
    {
      title: t('system-settings.battery.status.capacity-throughput.title'),
      textI18nKey: 'system-settings.battery.status.capacity-throughput.text',
      valueKeys: {
        capacityThroughput: {
          highVoltage: 'system-settings.battery.status.capacity-throughput.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.capacity-throughput.text-low-voltage',
        },
      },
      values: {
        capacityThroughput: batteryHealth.capacityThroughput,
      },
    },
    {
      title: t('system-settings.battery.status.energy-throughput.title'),
      textI18nKey: 'system-settings.battery.status.energy-throughput.text',
      valueKeys: {
        energyThroughput: {
          highVoltage: 'system-settings.battery.status.energy-throughput.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.energy-throughput.text-low-voltage',
        },
      },
      values: {
        energyThroughput: batteryHealth.energyThroughput,
      },
    },
    {
      title: t('system-settings.battery.status.remaining-power.title'),
      textI18nKey: 'system-settings.battery.status.remaining-power.text',
      valueKeys: {
        remainingPowerCharge: {
          highVoltage: 'system-settings.battery.status.remaining-power.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.remaining-power.text-low-voltage',
        },
        remainingPowerDischarge: {
          highVoltage: 'system-settings.battery.status.remaining-power-discharge.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.remaining-power-discharge.text-low-voltage',
        },
      },
      values: {
        remainingPowerCharge: batteryHealth.remainingPowerCapabilityCharge,
        remainingPowerDischarge: batteryHealth.remainingPowerCapabilityDischarge,
      },
    },
    {
      title: t('system-settings.battery.status.remaining-efficiency.title'),
      textI18nKey: 'system-settings.battery.status.remaining-efficiency.text',
      valueKeys: {
        remainingEfficiency: {
          highVoltage: 'system-settings.battery.status.remaining-efficiency.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.remaining-efficiency.text-low-voltage',
        },
      },
      values: {
        remainingEfficiency: batteryHealth.remainingEfficiency,
      },
    },
    {
      title: t('system-settings.battery.status.ohmic-resistance.title'),
      textI18nKey: 'system-settings.battery.status.ohmic-resistance.text',
      valueKeys: {
        ohmicResistance: {
          highVoltage: 'system-settings.battery.status.ohmic-resistance.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.ohmic-resistance.text-low-voltage',
        },
      },
      values: {
        ohmicResistance: batteryHealth.ohmicResistance,
      },
    },
    {
      title: t('system-settings.battery.status.self-discharge-rates.title'),
      textI18nKey: 'system-settings.battery.status.self-discharge-rates.text',
      valueKeys: {
        selfDischargeRates: {
          highVoltage: 'system-settings.battery.status.self-discharge-rates.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.self-discharge-rates.text-low-voltage',
        },
      },
      values: {
        selfDischargeRates: batteryHealth.selfDischargeRates,
      },
    },
    {
      title: t('system-settings.battery.status.manufacture-date.title'),
      textI18nKey: 'system-settings.battery.status.manufacture-date.text',
      valueKeys: {
        manufactureDate: {
          highVoltage: 'system-settings.battery.status.manufacture-date.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.manufacture-date.text-low-voltage',
        },
      },
      values: {
        manufactureDate: batteryHealth.dateOfManufacture,
      },
    },
    {
      title: t('system-settings.battery.status.commissioning-date.title'),
      textI18nKey: 'system-settings.battery.status.commissioning-date.text',
      valueKeys: {
        commissioningDate: {
          highVoltage: 'system-settings.battery.status.commissioning-date.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.commissioning-date.text-low-voltage',
        },
      },
      values: {
        commissioningDate: batteryHealth.dateOfCommissioning,
      },
    },
    {
      title: t('system-settings.battery.status.harmful-events.title'),
      textI18nKey: 'system-settings.battery.status.harmful-events.text',
      valueKeys: {
        harmfulEventsDeepDischargeEvents: {
          highVoltage: 'system-settings.battery.status.deep-discharge-events.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.deep-discharge-events.text-low-voltage',
        },
        harmfulEventsTimeTempExtremeHigh: {
          highVoltage: 'system-settings.battery.status.time-temp-extreme-high.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.time-temp-extreme-high.text-low-voltage',
        },
        harmfulEventsTimeTempExtremeHighCharge: {
          highVoltage: 'system-settings.battery.status.time-temp-extreme-high-charge.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.time-temp-extreme-high-charge.text-low-voltage',
        },
        harmfulEventsOverchargeEvents: {
          highVoltage: 'system-settings.battery.status.overcharge-events.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.overcharge-events.text-low-voltage',
        },
        harmfulEventsTimeTempExtremeLowCharge: {
          highVoltage: 'system-settings.battery.status.time-temp-extreme-low-charge.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.time-temp-extreme-low-charge.text-low-voltage',
        },
        harmfulEventsTimeDeepDischarge: {
          highVoltage: 'system-settings.battery.status.deep-discharge.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.deep-discharge.text-low-voltage',
        },
        harmfulEventsTimeOvercharge: {
          highVoltage: 'system-settings.battery.status.time-overcharge.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.time-overcharge.text-low-voltage',
        },
        harmfulEventsTimeOvercurrentDischarge: {
          highVoltage: 'system-settings.battery.status.time-overcurrent-discharge.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.time-overcurrent-discharge.text-low-voltage',
        },
        harmfulEventsTimeTempExtremeLow: {
          highVoltage: 'system-settings.battery.status.time-temp-extreme-low.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.time-temp-extreme-low.text-low-voltage',
        },
        harmfulEventsTimeOvercurrentCharge: {
          highVoltage: 'system-settings.battery.status.time-overcurrent-charge.text-high-voltage',
          lowVoltage: 'system-settings.battery.status.time-overcurrent-charge.text-low-voltage',
        },
      },
      values: {
        harmfulEventsDeepDischargeEvents: batteryHealth.harmfulEventsDeepDischargeEvents,
        harmfulEventsOverchargeEvents: batteryHealth.harmfulEventsOverchargeEvents,
        harmfulEventsTimeDeepDischarge: batteryHealth.harmfulEventsTimeDeepDischarge,
        harmfulEventsTimeOvercharge: batteryHealth.harmfulEventsTimeOvercharge,
        harmfulEventsTimeTempExtremeHigh: batteryHealth.harmfulEventsTimeTempExtremeHigh,
        harmfulEventsTimeTempExtremeLow: batteryHealth.harmfulEventsTimeTempExtremeLow,
        harmfulEventsTimeTempExtremeHighCharge: batteryHealth.harmfulEventsTimeTempExtremeHighCharge,
        harmfulEventsTimeTempExtremeLowCharge: batteryHealth.harmfulEventsTimeTempExtremeLowCharge,
        harmfulEventsTimeOvercurrentDischarge: batteryHealth.harmfulEventsTimeOvercurrentDischarge,
        harmfulEventsTimeOvercurrentCharge: batteryHealth.harmfulEventsTimeOvercurrentCharge,
      },
    },
  ]

  return { statusItems, isLowVoltageBattery }
}
